/**
 * Exposure Columns ECharts Adapter
 *
 * Renders category columns for exposure data with optional min/max range bands,
 * portfolio dots (one per resource), and target markers.
 */

import type { EChartsCoreOption } from "echarts";
import type {
  ExposureColumnsAdapterProps,
  EChartAdapterResult,
  ExposureAPIResponse,
  ExposureResponseItem,
  ExposureBandsMap,
} from "./types";
import { isBandsCompleteForCategories } from "../../hooks/useExposureBands";

const RANGE_COLOR = "rgba(248, 113, 113, 0.55)";
const TARGET_SYMBOL = "path://M-6,-6 L6,6 M6,-6 L-6,6";

function getDisplayName(
  resourceId: string,
  assetDisplayNames: Record<string, string>
): string {
  const idParts = resourceId.split(":");
  const childId = idParts.length > 1 ? idParts[1] : resourceId;
  return assetDisplayNames[childId] || assetDisplayNames[resourceId] || resourceId;
}

function getResourceColor(
  resourceId: string,
  assetColors: Record<string, string>,
  colorPalette: string[],
  index: number
): string {
  const idParts = resourceId.split(":");
  const childId = idParts.length > 1 ? idParts[1] : resourceId;
  return (
    assetColors[childId] ||
    assetColors[resourceId] ||
    colorPalette[index % colorPalette.length]
  );
}

function parseExposureResponse(apiResponse: ExposureAPIResponse): {
  resourceIds: string[];
  categories: string[];
  resourceDataMap: Map<string, Map<string, number>>;
} | null {
  const resourceIds = Object.keys(apiResponse).filter((key) => {
    const entry = apiResponse[key];
    return (
      key !== "metadata" &&
      !key.startsWith("_") &&
      entry &&
      typeof entry === "object" &&
      "rows" in entry
    );
  });

  if (resourceIds.length === 0) return null;

  const allCategories = new Set<string>();
  const resourceDataMap = new Map<string, Map<string, number>>();

  resourceIds.forEach((resourceId) => {
    const resourceData = apiResponse[resourceId] as ExposureResponseItem;
    if (!resourceData?.rows || !Array.isArray(resourceData.rows)) return;

    const categoryMap = new Map<string, number>();
    resourceData.rows.forEach((row) => {
      if (!Array.isArray(row) || row.length < 2) return;
      const category = String(row[0]);
      const value = row.length >= 4 ? Number(row[3]) : Number(row[1]);
      if (category && !Number.isNaN(value)) {
        allCategories.add(category);
        categoryMap.set(category, value);
      }
    });
    resourceDataMap.set(resourceId, categoryMap);
  });

  const categoryOrder = [
    "Stock",
    "Bond",
    "Cash",
    "Convertible",
    "Other",
    "Preferred",
    "Unclassified",
  ];
  const categories = Array.from(allCategories).sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a);
    const bIndex = categoryOrder.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return { resourceIds, categories, resourceDataMap };
}

/** Extract sorted category names from raw playground exposure data */
export function extractExposureCategories(
  rawData: unknown[] | null | undefined
): string[] {
  if (!rawData || rawData.length === 0) return [];
  const apiResponse = rawData[0] as ExposureAPIResponse;
  const parsed = parseExposureResponse(apiResponse);
  return parsed?.categories ?? [];
}

function buildAxisStyles(isDarkMode: boolean) {
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const gridColor = isDarkMode ? "#475569" : "#e2e8f0";
  const axisLineColor = isDarkMode ? "#64748b" : "#94a3b8";
  return { textColor, gridColor, axisLineColor };
}

function buildValueColumnsOption(
  categories: string[],
  resourceIds: string[],
  resourceDataMap: Map<string, Map<string, number>>,
  assetDisplayNames: Record<string, string>,
  assetColors: Record<string, string>,
  colorPalette: string[],
  isDarkMode: boolean,
  showTooltips: boolean,
  showGridRows: boolean,
  showXAxis: boolean,
  showYAxis: boolean
): EChartsCoreOption {
  const { textColor, gridColor, axisLineColor } = buildAxisStyles(isDarkMode);

  const series = resourceIds.map((resourceId, index) => {
    const categoryMap = resourceDataMap.get(resourceId);
    const data = categories.map((category) => {
      const value = categoryMap?.get(category) ?? 0;
      return Number((value * 100).toFixed(2));
    });

    return {
      name: getDisplayName(resourceId, assetDisplayNames),
      type: "bar" as const,
      data,
      itemStyle: {
        color: getResourceColor(resourceId, assetColors, colorPalette, index),
      },
      emphasis: { focus: "series" as const },
    };
  });

  return {
    backgroundColor: "transparent",
    tooltip: showTooltips
      ? {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: isDarkMode
            ? "rgba(30, 41, 59, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: { color: textColor },
        }
      : undefined,
    legend: { show: false },
    grid: {
      left: showYAxis ? 60 : 20,
      right: 20,
      top: 30,
      bottom: showXAxis ? (categories.length > 4 ? 80 : 60) : 30,
      containLabel: false,
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { show: showXAxis, lineStyle: { color: axisLineColor } },
      axisTick: { show: false },
      axisLabel: {
        show: showXAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        interval: 0,
        rotate: categories.length > 4 ? 30 : 0,
        margin: 10,
        formatter: (value: string) =>
          value.length > 18 ? `${value.substring(0, 15)}...` : value,
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      axisLine: { show: showYAxis },
      axisTick: { show: false },
      axisLabel: {
        show: showYAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => `${value}%`,
        margin: 12,
      },
      splitLine: {
        show: showGridRows,
        lineStyle: { color: gridColor, opacity: 0.3 },
      },
    },
    series,
  };
}

function buildRangeOverlayOption(
  categories: string[],
  resourceIds: string[],
  resourceDataMap: Map<string, Map<string, number>>,
  bands: ExposureBandsMap,
  assetDisplayNames: Record<string, string>,
  assetColors: Record<string, string>,
  colorPalette: string[],
  isDarkMode: boolean,
  showTooltips: boolean,
  showGridRows: boolean,
  showXAxis: boolean,
  showYAxis: boolean
): EChartsCoreOption {
  const { textColor, gridColor, axisLineColor } = buildAxisStyles(isDarkMode);
  const targetColor = isDarkMode ? "#e2e8f0" : "#334155";

  const minData = categories.map((cat) => bands[cat]?.min ?? 0);
  const rangeData = categories.map((cat) => {
    const band = bands[cat];
    return band ? band.max - band.min : 0;
  });
  const targetData = categories.map((cat) => {
    const band = bands[cat];
    return band ? [cat, band.target] : [cat, 0];
  });

  const portfolioSeries = resourceIds.map((resourceId, resIndex) => {
    const categoryMap = resourceDataMap.get(resourceId);

    const data = categories.map((category) => {
      const value = (categoryMap?.get(category) ?? 0) * 100;
      return [category, value];
    });

    return {
      name: getDisplayName(resourceId, assetDisplayNames),
      type: "scatter" as const,
      data,
      symbol: "circle",
      symbolSize: 10,
      itemStyle: {
        color: getResourceColor(resourceId, assetColors, colorPalette, resIndex),
      },
      z: 3,
    };
  });

  const series: EChartsCoreOption["series"] = [
    {
      name: "Range Base",
      type: "bar",
      stack: "range",
      showInLegend: false,
      data: minData,
      itemStyle: {
        borderColor: "transparent",
        color: "transparent",
      },
      emphasis: {
        itemStyle: {
          borderColor: "transparent",
          color: "transparent",
        },
      },
      tooltip: { show: false },
      z: 1,
    },
    {
      name: "Range",
      type: "bar",
      stack: "range",
      data: rangeData,
      barWidth: "40%",
      itemStyle: {
        color: RANGE_COLOR,
        borderRadius: [2, 2, 2, 2],
      },
      z: 1,
    },
    ...portfolioSeries,
    {
      name: "Target",
      type: "scatter",
      data: targetData,
      symbol: TARGET_SYMBOL,
      symbolSize: 14,
      itemStyle: {
        color: targetColor,
        borderColor: targetColor,
      },
      z: 4,
    },
  ];

  return {
    backgroundColor: "transparent",
    tooltip: showTooltips
      ? {
          trigger: "item",
          backgroundColor: isDarkMode
            ? "rgba(30, 41, 59, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: { color: textColor },
          formatter: (params: {
            seriesName?: string;
            name?: string;
            value?: number | [string, number] | [number, number];
          }) => {
            if (params.seriesName === "Range") {
              const idx = categories.indexOf(String(params.name));
              if (idx >= 0) {
                const band = bands[categories[idx]];
                return `${categories[idx]}<br/>Range: ${band.min}% – ${band.max}%`;
              }
            }
            if (params.seriesName === "Target") {
              const val = Array.isArray(params.value)
                ? params.value[1]
                : params.value;
              return `Target: ${val}%`;
            }
            if (params.seriesName && params.seriesName !== "Range Base") {
              const val = Array.isArray(params.value)
                ? params.value[1]
                : params.value;
              return `${params.seriesName}: ${val}%`;
            }
            return "";
          },
        }
      : undefined,
    legend: { show: false },
    grid: {
      left: showYAxis ? 60 : 20,
      right: 20,
      top: 30,
      bottom: showXAxis ? (categories.length > 4 ? 80 : 60) : 30,
      containLabel: false,
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { show: showXAxis, lineStyle: { color: axisLineColor } },
      axisTick: { show: false },
      axisLabel: {
        show: showXAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        interval: 0,
        rotate: categories.length > 4 ? 30 : 0,
        margin: 10,
        formatter: (value: string) =>
          value.length > 18 ? `${value.substring(0, 15)}...` : value,
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      axisLine: { show: showYAxis },
      axisTick: { show: false },
      axisLabel: {
        show: showYAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => `${value}%`,
        margin: 12,
      },
      splitLine: {
        show: showGridRows,
        lineStyle: { color: gridColor, opacity: 0.3 },
      },
    },
    series,
  };
}

export function buildExposureColumnsOption(
  props: ExposureColumnsAdapterProps
): EChartAdapterResult {
  const {
    rawData,
    assetDisplayNames,
    assetColors,
    colorPalette,
    visualSettings,
    isDarkMode,
    exposureBands,
  } = props;

  const showTooltips = visualSettings.showTooltips ?? true;
  const showGridRows = visualSettings.showGridRows ?? true;
  const showXAxis = visualSettings.showXAxis ?? true;
  const showYAxis = visualSettings.showYAxis ?? true;

  if (!rawData || rawData.length === 0) {
    return { option: {}, chartType: "bar", hasData: false };
  }

  const apiResponse = rawData[0] as unknown as ExposureAPIResponse;
  const parsed = parseExposureResponse(apiResponse);

  if (!parsed || parsed.categories.length === 0) {
    return { option: {}, chartType: "bar", hasData: false };
  }

  const { resourceIds, categories, resourceDataMap } = parsed;
  const useRangeMode = isBandsCompleteForCategories(categories, exposureBands);

  const option = useRangeMode
    ? buildRangeOverlayOption(
        categories,
        resourceIds,
        resourceDataMap,
        exposureBands!,
        assetDisplayNames,
        assetColors,
        colorPalette,
        isDarkMode,
        showTooltips,
        showGridRows,
        showXAxis,
        showYAxis
      )
    : buildValueColumnsOption(
        categories,
        resourceIds,
        resourceDataMap,
        assetDisplayNames,
        assetColors,
        colorPalette,
        isDarkMode,
        showTooltips,
        showGridRows,
        showXAxis,
        showYAxis
      );

  return {
    option,
    chartType: "bar",
    hasData: true,
  };
}
